import QRCode from 'qrcode';

// Framed QR card (heading, subtext, QR, footer) drawn on a canvas; shared by the QR generator and event QR PDF.
export const SIZES = { small: 600, medium: 900, large: 1400 };
const FONT = '"Inter", "Helvetica Neue", Arial, sans-serif';

// Greedy word wrap; very long words are split so nothing overflows the card.
function wrapLines(ctx, text, maxWidth) {
  const lines = [];
  String(text).split('\n').forEach((paragraph) => {
    let line = '';
    paragraph.split(/\s+/).filter(Boolean).forEach((word) => {
      let rest = word;
      while (ctx.measureText(rest).width > maxWidth && rest.length > 1) {
        let cut = rest.length - 1;
        while (cut > 1 && ctx.measureText(rest.slice(0, cut)).width > maxWidth) cut -= 1;
        if (line) {
          lines.push(line);
          line = '';
        }
        lines.push(rest.slice(0, cut));
        rest = rest.slice(cut);
      }
      const candidate = line ? `${line} ${rest}` : rest;
      if (ctx.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = rest;
      } else {
        line = candidate;
      }
    });
    lines.push(line);
  });
  return lines.filter((l, i, all) => l || (i > 0 && i < all.length - 1));
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function drawQrCard(canvas, options) {
  const width = SIZES[options.size] || SIZES.medium;
  const pad = Math.round(width * 0.08);
  const qrSize = width - pad * 2;
  const headingSize = Math.round(width * 0.062);
  const subtextSize = Math.round(width * 0.04);
  const footerSize = Math.round(width * 0.032);
  const gap = Math.round(width * 0.035);

  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, options.content, {
    width: qrSize,
    margin: 1,
    errorCorrectionLevel: options.errorLevel,
    color: { dark: options.foreground, light: options.background },
  });

  const ctx = canvas.getContext('2d');
  const measure = (text, font) => {
    ctx.font = font;
    return text ? wrapLines(ctx, text, qrSize) : [];
  };
  const headingFont = `700 ${headingSize}px ${FONT}`;
  const subtextFont = `400 ${subtextSize}px ${FONT}`;
  const footerFont = `600 ${footerSize}px ${FONT}`;
  const headingLines = measure(options.heading.trim(), headingFont);
  const subtextLines = measure(options.subtext.trim(), subtextFont);
  const footerLines = measure(options.footer.trim(), footerFont);

  const block = (lines, size) => (lines.length ? lines.length * size * 1.25 : 0);
  const top = block(headingLines, headingSize) + (headingLines.length ? gap * 0.6 : 0)
    + block(subtextLines, subtextSize) + (headingLines.length || subtextLines.length ? gap : 0);
  const bottom = footerLines.length ? gap + block(footerLines, footerSize) : 0;
  const height = pad + top + qrSize + bottom + pad;

  canvas.width = width;
  canvas.height = Math.round(height);
  ctx.fillStyle = options.background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (options.frame) {
    const inset = Math.round(width * 0.02);
    ctx.lineWidth = Math.max(4, Math.round(width * 0.008));
    ctx.strokeStyle = options.accent;
    roundRect(ctx, inset, inset, canvas.width - inset * 2, canvas.height - inset * 2, Math.round(width * 0.04));
    ctx.stroke();
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  let y = pad;
  const writeLines = (lines, font, size, color) => {
    ctx.font = font;
    ctx.fillStyle = color;
    lines.forEach((line) => {
      ctx.fillText(line, width / 2, y);
      y += size * 1.25;
    });
  };
  writeLines(headingLines, headingFont, headingSize, options.accent);
  if (headingLines.length) y += gap * 0.6;
  writeLines(subtextLines, subtextFont, subtextSize, options.foreground);
  if (headingLines.length || subtextLines.length) y += gap;

  ctx.drawImage(qrCanvas, pad, y, qrSize, qrSize);
  y += qrSize + gap;
  writeLines(footerLines, footerFont, footerSize, options.foreground);
}
